import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Menu, Sun, Moon, Grid3x3, Rows, Notebook, Archive, Trash2, Bell, Coffee, Send, X } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import QuickCreate from "@/components/QuickCreate";
import NoteCard from "@/components/NoteCard";
import NoteEditor from "@/components/NoteEditor";
import LabelsDialog from "@/components/LabelsDialog";
import TipJarDialog from "@/components/TipJarDialog";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import InstallButton from "@/components/InstallButton";
import ShareDialog from "@/components/ShareDialog";
import { BrandMark } from "@/components/Brand";
import { hasGuestShares, syncGuestShares } from "@/lib/guestShareSync";
import MobileFab from "@/components/MobileFab";
import {
  listNotes, createNote as createNoteRemote, updateNote as updateNoteRemote,
  deleteNote as deleteNoteRemote, emptyTrash as emptyTrashRemote, listLabels,
  filterNotes, subscribe as subscribeStore,
} from "@/lib/offlineStore";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useT } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { useReminders, dueTodayCount } from "@/lib/reminders";
import { cn } from "@/lib/utils";

const VIEW_META = {
  notes:     { icon: Notebook, emptyKey: "empty.notes",     titleKey: "nav.notes" },
  reminders: { icon: Bell,      emptyKey: "empty.reminders", titleKey: "nav.reminders" },
  archive:   { icon: Archive,   emptyKey: "empty.archive",   titleKey: "nav.archive" },
  trash:     { icon: Trash2,    emptyKey: "empty.trash",     titleKey: "nav.trash" },
};

export default function KeepApp() {
  const { t } = useT();
  const [view, setView] = useState("notes");
  const [notes, setNotes] = useState([]);
  const [labels, setLabels] = useState([]);
  const [search, setSearch] = useState("");
  const [grid, setGrid] = useState(() => {
    // Persist the grid/list toggle so a user doesn't flip it by accident
    // and lose their preferred layout on every refresh. One-time 2026-02
    // migration: an earlier version left users accidentally stuck in list
    // mode because the "+" FAB's tap region overlapped the view-toggle on
    // phones. The `mango.view-grid.v2` key resets everyone to grid once.
    try {
      if (!localStorage.getItem("mango.view-grid.v2")) {
        localStorage.setItem("mango.view-grid.v2", "1");
        localStorage.setItem("mango.view-grid", "true");
        return true;
      }
      return localStorage.getItem("mango.view-grid") !== "false";
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("mango.view-grid", grid ? "true" : "false"); } catch {}
  }, [grid]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [sharing, setSharing] = useState(null);
  const [dark, setDark] = useDarkMode();
  const { online, pending } = useOnlineStatus();
  const [loading, setLoading] = useState(true);
  const { authState } = useAuth();

  const activeView = view.startsWith("label:") ? "notes" : view;
  const activeLabel = view.startsWith("label:") ? view.slice(6) : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { view: activeView };
      if (activeLabel) params.label = activeLabel;
      if (search.trim()) params.q = search.trim();
      const [n, l] = await Promise.all([listNotes(params), listLabels()]);
      // listNotes already filters by view; apply label+search locally for consistency
      setNotes(filterNotes(activeView, activeLabel, search.trim()));
      setLabels(l);
    } finally { setLoading(false); }
  }, [activeView, activeLabel, search]);

  useEffect(() => { load(); }, [load]);

  // Re-run `load()` once auth resolves — the first mount fires with
  // authState=null so the local store short-circuits and we see an empty
  // shell. When auth lands as "user", pull remote notes/labels; as "guest",
  // the local cache stays the source of truth.
  useEffect(() => { if (authState) load(); }, [authState, load]);

  // Background sync so collaborator saves made via a share link show up on
  // the owner's note cards without a manual refresh. Only runs for signed-in
  // users while the tab is visible to avoid wasted traffic and guest 401s.
  useEffect(() => {
    if (!authState) return;
    const guest = authState !== "user";
    const tick = async () => {
      if (document.visibilityState !== "visible") return;
      if (guest) {
        if (!hasGuestShares()) return;
        await syncGuestShares().catch(() => {});
      }
      load();
    };
    if (guest) tick();
    let id;
    const start = () => {
      stop();
      id = setInterval(tick, 20000);
    };
    const stop = () => { if (id) { clearInterval(id); id = null; } };
    const onVis = () => (document.visibilityState === "visible" ? start() : stop());
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [load, authState]);

  // Re-read local store whenever it emits changes
  useEffect(() => {
    const unsub = subscribeStore(() => {
      setNotes(filterNotes(activeView, activeLabel, search.trim()));
      // Also refresh labels — the inline "Create new label…" flow inside
      // NoteEditor adds to the store but the parent needs this re-fetch
      // to show the new label in the sidebar + inside the editor dropdown.
      listLabels().then(setLabels).catch(() => {});
    });
    return unsub;
  }, [activeView, activeLabel, search]);

  // (Voice-note transcription has been removed — users reported the
  // Whisper flow was unreliable across devices. The audio recorder now
  // just saves the clip as a plain attachment; no background event is
  // needed.)

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        document.querySelector("[data-testid='search-input']")?.focus();
      }
      if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        document.querySelector("[data-testid='search-input']")?.focus();
      }
      if (e.key === "Escape" && document.activeElement?.getAttribute?.("data-testid") === "search-input") {
        setSearch("");
        document.activeElement.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const createNote = async (payload) => {
    const created = await createNoteRemote(payload);
    toast.success(t("toasts.noteCreated"));
    return created;
  };
  const updateNote = async (id, patch) => {
    await updateNoteRemote(id, patch, {
      onConflict: () => toast.message(t("conflict.toast"), { duration: 5000 }),
    });
  };

  // Reminders: toast + browser notification when a reminder's time arrives;
  // for recurring reminders, auto-advance to the next occurrence so the same
  // note fires again next cycle without the user editing it.
  const rescheduleReminder = useCallback((id, nextIso) => {
    updateNoteRemote(id, { reminder: nextIso }).catch(() => {});
  }, []);
  useReminders(notes, { onReschedule: rescheduleReminder });
  const dueToday = dueTodayCount(notes);
  const trashNote = async (id) => {
    await updateNoteRemote(id, { trashed: true, archived: false });
    toast.success(t("toasts.movedToTrash"));
  };
  const archiveNote = async (id, archived) => {
    await updateNoteRemote(id, { archived });
    toast.success(archived ? t("toasts.archived") : t("toasts.unarchived"));
  };
  const restoreNote = async (id) => {
    await updateNoteRemote(id, { trashed: false });
    toast.success(t("toasts.restored"));
  };
  const permanentDelete = async (id) => {
    await deleteNoteRemote(id);
    toast.success(t("toasts.deleted"));
  };
  const emptyTrash = async () => {
    await emptyTrashRemote();
    toast.success(t("toasts.trashEmptied"));
  };

  // Drag-and-drop target handler called by Sidebar when a note card is
  // dropped onto Archive / Trash / Reminders / a label chip.
  const dropNoteOn = async (noteId, action, labelName) => {
    const note = notes.find((n) => n.id === noteId);
    if (!note) return;
    try {
      if (action === "archive") {
        await updateNoteRemote(noteId, { archived: true, trashed: false });
        toast.success(t("toasts.archived"));
      } else if (action === "trash") {
        await updateNoteRemote(noteId, { trashed: true, archived: false });
        toast.success(t("toasts.movedToTrash"));
      } else if (action === "label" && labelName) {
        const next = (note.labels || []).includes(labelName)
          ? note.labels
          : [...(note.labels || []), labelName];
        await updateNoteRemote(noteId, { labels: next });
        toast.success(`Added to “${labelName}”`);
      }
    } catch (err) {
      toast.error("Could not move note");
    }
  };

  const { pinned, others } = useMemo(() => {
    const p = [], o = [];
    for (const n of notes) (n.pinned ? p : o).push(n);
    return { pinned: p, others: o };
  }, [notes]);

  const Meta = VIEW_META[activeView];
  const headerTitle = activeLabel ? activeLabel : t(Meta.titleKey);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="flex items-center gap-3 px-4 h-16">
          <button
            className="md:hidden h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800"
            onClick={() => setSidebarOpen(true)}
            data-testid="open-sidebar-btn"
            aria-label={t("top.openSidebar")}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <BrandMark />
            <span className="font-display font-bold text-lg hidden sm:inline">Mango Notes</span>
          </div>

          <div className="flex-1 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
              <Input
                placeholder={t("top.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && search.trim() && notes.length > 0) {
                    e.preventDefault();
                    setEditing(notes[0]);
                  }
                }}
                className="pl-10 pr-20 rounded-2xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 shadow-sm focus-visible:ring-amber-500/50 focus-visible:border-stone-300 dark:focus-visible:border-stone-600 h-11"
                data-testid="search-input"
              />
              {search ? (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-full bg-stone-200 dark:bg-stone-700 hover:bg-stone-300 dark:hover:bg-stone-600 text-stone-700 dark:text-stone-200"
                  aria-label={t("top.clear")}
                  data-testid="search-clear"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : (
                <kbd className="hidden sm:inline-flex absolute right-3 top-1/2 -translate-y-1/2 font-mono-xs text-stone-400 border border-border rounded px-1.5 py-0.5">⌘K</kbd>
              )}
            </div>
          </div>

          {/* "Share with a friend" moved out of the header — now lives in
              the sidebar (both desktop sticky + mobile overlay). Avoids
              header overflow on mobile and keeps the top row focused on
              navigation + search. */}

          <LanguageSwitcher />
          <InstallButton />

          <button
            onClick={() => setTipOpen(true)}
            className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-gradient-to-r from-amber-500 to-rose-500 text-white font-semibold text-sm shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
            data-testid="tip-jar-btn"
          >
            <Coffee className="h-4 w-4" />
            {t("top.buyCoffee")}
          </button>
          <button
            onClick={() => setTipOpen(true)}
            className="sm:hidden h-10 w-10 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-rose-500 text-white"
            data-testid="tip-jar-btn-mobile"
            aria-label={t("top.buyCoffee")}
          >
            <Coffee className="h-5 w-5" />
          </button>

          <button
            onClick={() => setGrid((g) => !g)}
            className="hidden md:inline-flex shrink-0 h-10 w-10 items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800"
            data-testid="toggle-view"
            aria-label={t("top.toggleView")}
          >
            {grid ? <Rows className="h-5 w-5" /> : <Grid3x3 className="h-5 w-5" />}
          </button>
          <button
            onClick={() => setDark(!dark)}
            className="shrink-0 h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800"
            data-testid="toggle-theme"
            aria-label={t("top.toggleTheme")}
          >
            {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <div className="flex-1 flex">
        <Sidebar
          view={view}
          setView={setView}
          labels={labels}
          onManageLabels={() => setLabelsOpen(true)}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onDropNote={dropNoteOn}
        />

        <main className="flex-1 min-w-0">
          {activeView === "notes" && !activeLabel && (
            <QuickCreate
              onCreate={createNote}
              onArchive={archiveNote}
              onTrash={trashNote}
              onShare={setSharing}
              labels={labels}
            />
          )}

          {activeView === "notes" && !activeLabel && dueToday > 0 && (
            <div
              className="mx-4 mt-3 mb-1 max-w-7xl md:mx-auto flex items-center gap-3 rounded-xl border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 px-4 py-2.5 text-sm cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors"
              onClick={() => setView("reminders")}
              data-testid="due-today-banner"
              role="button"
            >
              <Bell className="h-4 w-4 shrink-0 fill-current" />
              <span className="font-medium">
                {dueToday === 1 ? "1 reminder due today" : `${dueToday} reminders due today`}
              </span>
              <span className="ml-auto text-xs opacity-70">Click to view →</span>
            </div>
          )}

          <div className="px-4 pt-4 pb-8 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h1 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2" data-testid="view-title">
                <Meta.icon className="h-6 w-6 text-stone-500" />
                {headerTitle}
                <span className="font-mono-xs text-stone-500 dark:text-stone-400 ml-1">{notes.length}</span>
              </h1>

              <div className="flex items-center gap-2">
                {search.trim() && (
                  <button
                    onClick={() => setSearch("")}
                    data-testid="search-chip"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-xs font-medium hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors"
                    title={t("top.clear")}
                  >
                    <Search className="h-3 w-3" />
                    <span className="max-w-[180px] truncate">{t("top.searchActive", { n: notes.length, q: search.trim() })}</span>
                    <X className="h-3 w-3" />
                  </button>
                )}
                {activeView === "trash" && notes.length > 0 && (
                  <Button variant="outline" onClick={emptyTrash} data-testid="empty-trash-btn" className="rounded-full">
                    {t("actions.emptyTrash")}
                  </Button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="text-sm text-stone-500 py-12 text-center">...</div>
            ) : notes.length === 0 ? (
              <EmptyState
                title={search.trim() ? t("top.noResults", { q: search.trim() }) : t(Meta.emptyKey)}
                icon={search.trim() ? Search : Meta.icon}
              />
            ) : (
              <>
                {pinned.length > 0 && (
                  <>
                    <SectionLabel label={t("sections.pinned")} />
                    <NoteGrid grid={grid}>
                      <AnimatePresence>
                        {pinned.map((n) => (
                          <NoteCard key={n.id} note={n} labels={labels}
                            onUpdate={updateNote} onTrash={trashNote} onRestore={restoreNote}
                            onPermanentDelete={permanentDelete} onOpen={setEditing}
                            onShare={setSharing} view={activeView}
                            highlight={search.trim()} />
                        ))}
                      </AnimatePresence>
                    </NoteGrid>
                    {others.length > 0 && <SectionLabel label={t("sections.others")} />}
                  </>
                )}
                <NoteGrid grid={grid}>
                  <AnimatePresence>
                    {others.map((n) => (
                      <NoteCard key={n.id} note={n} labels={labels}
                        onUpdate={updateNote} onTrash={trashNote} onRestore={restoreNote}
                        onPermanentDelete={permanentDelete} onOpen={setEditing}
                        onShare={setSharing} view={activeView}
                        highlight={search.trim()} />
                    ))}
                  </AnimatePresence>
                </NoteGrid>
              </>
            )}
          </div>
        </main>
      </div>

      <NoteEditor
        note={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSave={updateNote}
        onTrash={trashNote}
        onArchive={archiveNote}
        onDeleteEmpty={(id) => deleteNoteRemote(id)}
        labels={labels}
        onDuplicated={() => load()}
        onManageLabels={() => setLabelsOpen(true)}
      />
      <LabelsDialog open={labelsOpen} onOpenChange={setLabelsOpen} labels={labels} onChanged={load} />
      <TipJarDialog open={tipOpen} onOpenChange={setTipOpen} />
      <ShareDialog note={sharing} open={!!sharing} onOpenChange={(v) => !v && setSharing(null)} />

      {activeView === "notes" && !activeLabel && (
        <MobileFab onCreate={createNote} onOpenEditor={setEditing} />
      )}
    </div>
  );
}

function SectionLabel({ label }) {
  return <p className="font-mono-xs text-stone-500 dark:text-stone-400 mb-3 mt-1 px-1">{label}</p>;
}

function NoteGrid({ grid, children }) {
  // Phones (<md) ALWAYS use 2-col masonry regardless of the user's grid
  // toggle — list mode on a 390 px screen produced an unreadable "stack
  // of giant cards" that users kept reporting as broken. Desktop still
  // honours the toggle: grid=masonry, list=max-w-3xl stacked column.
  if (grid) {
    return <div className="masonry mb-6">{children}</div>;
  }
  return (
    <>
      <div className="md:hidden masonry mb-6">{children}</div>
      <div className="hidden md:block max-w-3xl mx-auto space-y-3 mb-6">{children}</div>
    </>
  );
}

function EmptyState({ title, icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="empty-state">
      <div className="h-20 w-20 rounded-3xl bg-gradient-to-br from-amber-100 to-rose-100 dark:from-amber-900/40 dark:to-rose-900/40 flex items-center justify-center mb-4">
        <Icon className="h-9 w-9 text-amber-700 dark:text-amber-300" />
      </div>
      <p className="font-display text-lg text-stone-600 dark:text-stone-300">{title}</p>
    </div>
  );
}
